/* EMPRICS light preview v1.0 (28/10/26)
   Draws an indicative light pool, beam cone and selection chips for the code being built in the EMPRICS configurator.
   Reads the configurator's shared parser and register labels (window.EmpricsConfigurator); never edits the configurator.
   Colour temperature: blackbody approximation (Tanner Helland); beam cone and pool diameter from geometry:
   pool diameter = 2 x mounting height x tan(beam / 2). Pauses all motion under prefers-reduced-motion. */
(function () {
  'use strict';

  var HEIGHTS = [2.4, 2.7, 3, 4, 6, 8];
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function kelvinRGB(k) {
    var t = Math.max(1000, Math.min(40000, k)) / 100, r, g, b;
    if (t <= 66) { r = 255; g = 99.4708025861 * Math.log(t) - 161.1195681661; }
    else { r = 329.698727446 * Math.pow(t - 60, -0.1332047592); g = 288.1221695283 * Math.pow(t - 60, -0.0755148492); }
    if (t >= 66) b = 255; else if (t <= 19) b = 0; else b = 138.5177312231 * Math.log(t - 10) - 305.0447927307;
    var c = function (v) { return Math.round(Math.max(0, Math.min(255, v))); };
    return [c(r), c(g), c(b)];
  }
  function kelvinName(k) {
    if (k < 2400) return 'Candle warm';
    if (k < 2900) return 'Extra warm white';
    if (k < 3300) return 'Warm white';
    if (k < 3800) return 'Neutral warm';
    if (k < 4500) return 'Neutral white';
    if (k < 5600) return 'Cool white';
    return 'Daylight';
  }
  // Colour temperature from the code: numeric codes are hundreds of Kelvin; named codes read the register label.
  function cctInfo(code, label) {
    if (/^\d{2}$/.test(code) && code !== '00') { var k = parseInt(code, 10) * 100; return { k: k, from: k, to: k, text: k + 'K ' + kelvinName(k) }; }
    var nums = (label || '').match(/(\d{4})/g) || [];
    if (nums.length >= 2) {
      var a = parseInt(nums[0], 10), b = parseInt(nums[1], 10);
      return { k: Math.round((a + b) / 2), from: Math.min(a, b), to: Math.max(a, b), text: label, cycle: true };
    }
    if (nums.length === 1) { var n = parseInt(nums[0], 10); return { k: n, from: n, to: n, text: label }; }
    return label ? { k: 3500, from: 3000, to: 4000, text: label, cycle: /switch|tune/i.test(label) } : null;
  }
  // Beam from the code: numeric codes are degrees; named distributions map to an indicative shape.
  function beamInfo(code, label) {
    if (/^\d{2,3}$/.test(code) && !/^0+$/.test(code)) { var d = parseInt(code, 10); return { deg: d, text: d + '° beam' }; }
    var l = (label || '').toLowerCase();
    if (/asym|wall ?wash/.test(l)) return { deg: 70, asym: true, text: label, named: true };
    if (/batwing/.test(l)) return { deg: 110, bat: true, text: label, named: true };
    if (/narrow/.test(l)) return { deg: 20, text: label, named: true };
    if (/medium/.test(l)) return { deg: 50, text: label, named: true };
    if (/wide|flood|diffuse|opal/.test(l)) return { deg: 100, text: label, named: true };
    return label && !/^0+$|^X+$/.test(code) ? { deg: 90, text: label, named: true } : { deg: 110, diffuse: true, text: 'Diffuse distribution' };
  }
  function lumensFrom(desc) {
    var m = (desc || '').match(/c\/w\s*([\d,]+)\s*lm/i);
    return m ? parseInt(m[1].replace(/,/g, ''), 10) : null;
  }
  function defaultHeight(family) {
    var p = family.slice(0, 2);
    if (p === 'ID' || p === 'XF') return 6;
    return 2.7;
  }
  function el(tag, cls, text) { var e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }

  function Preview(cfgRoot) {
    this.cfg = cfgRoot;
    this.family = cfgRoot.dataset.family;
    this.height = defaultHeight(this.family);
    this.phase = 0;
    this.build();
    this.mount();
    var self = this;
    var codeEl = cfgRoot.querySelector('[data-cfg-code]');
    var descEl = cfgRoot.querySelector('[data-cfg-desc]');
    var obs = new MutationObserver(function () { self.update(); });
    if (codeEl) obs.observe(codeEl, { childList: true, characterData: true, subtree: true });
    if (descEl) obs.observe(descEl, { childList: true, characterData: true, subtree: true });
    window.addEventListener('resize', function () { self.mount(); });
    this.update();
  }

  Preview.prototype.build = function () {
    var self = this, w = this.wrap = el('figure', 'emp-lp');
    w.setAttribute('aria-label', 'Indicative light preview');
    var head = el('figcaption', 'emp-lp__head');
    head.appendChild(el('span', 'emp-lp__title', 'Light preview'));
    this.codeOut = el('span', 'emp-lp__code');
    head.appendChild(this.codeOut);
    w.appendChild(head);

    var stage = el('div', 'emp-lp__stage');
    var NS = 'http://www.w3.org/2000/svg';
    var svg = this.svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 400 200');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML =
      '<defs>' +
      '<linearGradient id="empLpCone" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff" stop-opacity=".9"/><stop offset="1" stop-color="#fff" stop-opacity=".22"/></linearGradient>' +
      '<radialGradient id="empLpPool"><stop offset="0" stop-color="#fff" stop-opacity="1"/><stop offset=".55" stop-color="#fff" stop-opacity=".55"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>' +
      '</defs>' +
      '<rect x="0" y="0" width="400" height="14" class="emp-lp__ceiling"/>' +
      '<rect x="0" y="168" width="400" height="32" class="emp-lp__floor"/>' +
      '<path class="emp-lp__cone" fill="url(#empLpCone)"/>' +
      '<ellipse class="emp-lp__pool" cx="200" cy="168" fill="url(#empLpPool)"/>' +
      '<rect class="emp-lp__fitting" x="190" y="10" width="20" height="7" rx="2"/>' +
      '<line class="emp-lp__dim" y1="176" y2="176"/>' +
      '<text class="emp-lp__dimtext" x="200" y="192" text-anchor="middle"></text>' +
      '<line class="emp-lp__hdim" x1="384" x2="384" y1="17" y2="166"/>' +
      '<text class="emp-lp__hdimtext" x="378" y="96" text-anchor="end"></text>';
    stage.appendChild(svg);
    w.appendChild(stage);

    var facts = this.facts = el('dl', 'emp-lp__facts');
    w.appendChild(facts);

    var hs = el('div', 'emp-lp__heights');
    hs.appendChild(el('span', 'emp-lp__hlabel', 'Mounting height'));
    HEIGHTS.forEach(function (h) {
      var b = el('button', 'emp-lp__h', h + ' m');
      b.type = 'button'; b.dataset.h = h;
      b.addEventListener('click', function () { self.height = h; self.update(); });
      hs.appendChild(b);
    });
    w.appendChild(hs);

    this.chips = el('ul', 'emp-lp__chips');
    w.appendChild(this.chips);
    w.appendChild(el('p', 'emp-lp__note', 'Indicative. Screen colour approximates the colour temperature; pool size follows the beam angle and mounting height.'));

    if (!reduce) this.animate();
  };

  // Desktop: under the main product image and its thumbnails in the sticky gallery. Narrow screens: top of the code panel.
  Preview.prototype.mount = function () {
    var wide = window.innerWidth >= 990;
    var hero = document.querySelector('.emprics-pdp__hero-wrap');
    var target, before = null;
    if (wide && hero && hero.parentNode) {
      // 01/11/26: photos and thumbnails first, light preview below them.
      var thumbs = hero.parentNode.querySelector('.emprics-pdp__thumbs');
      var anchor = (thumbs && thumbs.parentNode === hero.parentNode) ? thumbs : hero;
      target = hero.parentNode; before = anchor.nextSibling;
    }
    else { target = this.cfg.querySelector('.emp-cfg__result'); before = target ? target.firstChild : null; }
    if (!target) target = this.cfg;
    if (this.wrap.parentNode === target && this.wrap.nextSibling === before) return;
    target.insertBefore(this.wrap, before);
    this.wrap.classList.toggle('emp-lp--gallery', !!(wide && hero));
  };

  Preview.prototype.update = function () {
    var shared = window.EmpricsConfigurator || {};
    var parse = shared._parse;
    var codeText = (this.cfg.querySelector('[data-cfg-code]') || {}).textContent || '';
    if (!parse || !codeText || codeText.indexOf('.') < 0) { this.wrap.hidden = true; return; }
    this.wrap.hidden = false;
    var data = (shared.data || {})[this.family] || {};
    var labels = {};
    (data.labels || []).forEach(function (x) { (labels[x.p] = labels[x.p] || {})[x.c] = x.l; });
    var segs = codeText.split('.');
    var base = segs.slice(1).join('.');
    var em = base.slice(-1);
    var p = parse(base.slice(0, -1));
    var lab = function (k, v) { return (labels[k] && labels[k][v]) || ''; };

    this.state = {
      code: codeText,
      cct: p.cct ? cctInfo(p.cct, lab('cct', p.cct)) : null,
      beam: beamInfo(p.beam, lab('beam', p.beam)),
      cri: p.cri ? (lab('cri', p.cri) || ('CRI ' + p.cri + '0')) : '',
      driver: p.driver ? lab('driver', p.driver) : '',
      optic: lab('optic', p.optic),
      colour: lab('colour', p.colour),
      emergency: em && em !== '0' ? (lab('emergency', em) || 'Emergency') : '',
      lm: lumensFrom((this.cfg.querySelector('[data-cfg-desc]') || {}).textContent),
      lampHolder: /^\d{1,3}X/.test(p.wattage || '')
    };
    this.codeOut.textContent = codeText;
    this.draw();
    this.renderFacts();
  };

  Preview.prototype.draw = function () {
    var s = this.state; if (!s) return;
    var deg = s.beam.deg, half = Math.min(deg, 160) / 2 * Math.PI / 180;
    var topY = 17, floorY = 168, drop = floorY - topY;
    // Scene drawn to scale horizontally: the floor shows 6 m, or more for high mounting.
    var sceneW = Math.max(6, this.height * 2.4), pxPerM = 360 / sceneW;
    var spread = Math.min(185, Math.max(6, this.height * Math.tan(half) * pxPerM));
    var cx = 200, skew = s.beam.asym ? spread * 0.55 : 0;
    var cone = 'M' + (cx - 7) + ',' + topY + ' L' + (cx + 7) + ',' + topY + ' L' + (cx + spread + skew) + ',' + floorY + ' L' + (cx - spread + skew) + ',' + floorY + ' Z';
    var k = s.cct ? (s.cct.cycle ? s.cct.from + (s.cct.to - s.cct.from) * (0.5 - 0.5 * Math.cos(this.phase)) : s.cct.k) : 3500;
    var rgb = kelvinRGB(k), col = 'rgb(' + rgb.join(',') + ')';
    var bright = s.lm ? Math.max(0.45, Math.min(1, 0.45 + Math.log10(s.lm) / 8)) : 0.75;
    var q = this.svg.querySelector.bind(this.svg);
    q('.emp-lp__cone').setAttribute('d', cone);
    q('.emp-lp__cone').style.color = col;
    this.svg.querySelectorAll('#empLpCone stop').forEach(function (st) { st.setAttribute('stop-color', col); });
    this.svg.querySelectorAll('#empLpPool stop').forEach(function (st) { st.setAttribute('stop-color', col); });
    var pool = q('.emp-lp__pool');
    pool.setAttribute('cx', cx + skew);
    pool.setAttribute('rx', Math.max(14, spread * 1.08));
    pool.setAttribute('ry', 9);
    pool.style.opacity = bright;
    q('.emp-lp__cone').style.opacity = s.lampHolder ? 0.25 : 0.55 + bright * 0.4;
    // Geometry: pool diameter at the chosen mounting height.
    var dia = (s.beam.diffuse || s.beam.named) ? null : 2 * this.height * Math.tan(half);
    var dim = q('.emp-lp__dim'), dt = q('.emp-lp__dimtext');
    if (dia && deg < 170) {
      dim.setAttribute('x1', cx - spread + skew); dim.setAttribute('x2', cx + spread + skew); dim.style.display = '';
      dt.textContent = 'Pool Ø ' + dia.toFixed(2) + ' m';
    } else { dim.style.display = 'none'; dt.textContent = s.beam.named ? 'Indicative distribution' : 'Diffuse'; }
    q('.emp-lp__hdimtext').textContent = this.height + ' m';
    this.currentK = Math.round(k / 10) * 10;
  };

  Preview.prototype.renderFacts = function () {
    var s = this.state, f = this.facts, self = this;
    f.innerHTML = '';
    var add = function (t, v) { if (!v) return; f.appendChild(el('dt', null, t)); f.appendChild(el('dd', null, v)); };
    add('Colour temperature', s.cct ? s.cct.text : (s.lampHolder ? 'Set by the lamp' : ''));
    var dia = (s.beam.diffuse || s.beam.named) ? '' : ', Ø ' + (2 * this.height * Math.tan(Math.min(s.beam.deg, 160) / 2 * Math.PI / 180)).toFixed(2) + ' m pool at ' + this.height + ' m';
    add('Beam', s.beam.text + dia);
    add('Output', s.lm ? s.lm.toLocaleString('en-GB') + ' lm' : '');
    add('Colour rendering', s.cri);
    this.wrap.querySelectorAll('.emp-lp__h').forEach(function (b) {
      b.setAttribute('aria-pressed', parseFloat(b.dataset.h) === self.height ? 'true' : 'false');
    });
    var chips = [s.driver, s.optic, s.colour, s.emergency].filter(Boolean);
    this.chips.innerHTML = '';
    chips.forEach(function (c) { self.chips.appendChild(el('li', 'emp-lp__chip', c)); });
  };

  Preview.prototype.animate = function () {
    var self = this, last = 0;
    var tick = function (t) {
      if (self.state && self.state.cct && self.state.cct.cycle && t - last > 60) {
        last = t; self.phase += 0.06; self.draw();
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  function boot() {
    document.querySelectorAll('[data-emp-cfg]').forEach(function (root) {
      if (root.__empLp) return;
      var start = function () { if (!root.__empLp && root.isConnected) root.__empLp = new Preview(root); };
      // Wait for the register data so the parser and labels exist.
      if ((window.EmpricsConfigurator || {}).data && window.EmpricsConfigurator.data[root.dataset.family]) start();
      else document.addEventListener('emp-cfg:data', function (e) {
        if (e.detail && e.detail.family === root.dataset.family && e.detail.data) start();
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.EmpricsLightPreview = { boot: boot, kelvinRGB: kelvinRGB };
})();
